import React, { useState } from 'react'
import _ from 'underscore'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'

import Avatar from '@mui/material/Avatar'
import { styled as MuiStyled } from '@mui/material/styles'
import Menu from '@mui/material/Menu'

import Brand from 'helpers/brand'
import Environment from 'helpers/environment'
import WindowLocation from 'helpers/windowLocation'

import FeaturesModal from 'components/UserInfoMenu/FeaturesManager/FeaturesModal'
import KeyboardAccessible from 'components/KeyboardAccessible'
import RoleFeaturesModal from 'components/UserInfoMenu/FeaturesManager/RoleFeaturesModal'

import Spinner from 'components/Spinner'
import { useSessionContext } from 'hooks/SessionContext'
import { color } from 'styles/colors'
import { focusOutline } from 'styles/shared'

import useDealer from 'hooks/useDealer'

import Api from 'services/api'

const ID = 'menu-id'

type Props = {
  className?: string,
  displayMiniVariantSidebar: boolean
}

const UserInfoMenu = ({ className = '', displayMiniVariantSidebar }: Props) => {
  const { session: { userName, username, dealerName, dealerPhoneNumber, isDealer, isAdmin, isImpersonating } } = useSessionContext()
  const { dealer, isDealerLoaded } = useDealer()
  const { imageUrl } = dealer
  const [anchorEl, setAnchorEl] = useState<Element | null>(null)
  const navigate = useNavigate()
  const [displayFeatureCodesModal, setDisplayFeatureCodesModal] = useState(false)

  const initialLetters = (name: string) => {
    const nameParts = name.split(' ')

    if (nameParts.length > 1) {
      return `${nameParts[0][0]}${nameParts[1][0]}`
    }

    return name[0] // dealer name won't be null
  }

  const onClickDealerLogoButton = () => {
    setAnchorEl(null)
    navigate('/dealer_image')
  }

  const onLogoutClick = () => {
    if (Environment.isAWSCognito()) {
      const redirectUrl = WindowLocation.protocol() + '//' + WindowLocation.hostname() + '/signout'
      const signoutUrl = `${Environment.signOutURL()}?post_logout_redirect_uri=${redirectUrl}`

      WindowLocation.replace(signoutUrl)
    } else {
      WindowLocation.replace(Api.Session.logout)
    }
  }

  const onCloseFeaturesCallback = (reloadPage: boolean) => {
    setDisplayFeatureCodesModal(false)

    if (reloadPage) {
      navigate(0)
    }
  }

  if (!isDealerLoaded) {
    return <S.Spinner variant="page" />
  }

  return (
    <>
      <S.Button {...{ className }} onClick={e => setAnchorEl(e.currentTarget)} aria-controls={ID} aria-haspopup >
        {_.isEmpty(imageUrl)
          ? !_.isEmpty(userName) && <S.Avatar data-testid="avatar">{initialLetters(userName)}</S.Avatar>
          : <S.Image src={imageUrl} alt="" role="img" displayMini={displayMiniVariantSidebar} />
        }
        {!displayMiniVariantSidebar && (<S.Username>{userName}</S.Username>)}
        <S.CaretIcon className={`icon-arrow-${anchorEl ? 'up' : 'down2'}`} />
      </S.Button>
      <Menu {...{ anchorEl }} id={ID} onClose={() => setAnchorEl(null)} open={!!anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }} keepMounted
      >
        <S.NonExecutableMenuItem>Username: {username}</S.NonExecutableMenuItem>
        <S.NonExecutableMenuItem>
          {dealerName}
          <br />
          Code: {Brand.isTrane() ? dealerPhoneNumber && dealerPhoneNumber.replace(/-/g, '') : dealerPhoneNumber}
        </S.NonExecutableMenuItem>
        {
          isDealer &&
          <S.ExecutableMenuItem onClick={() => onClickDealerLogoButton()}>
            Dealer Logo
          </S.ExecutableMenuItem>
        }
        {
          (isAdmin || isDealer) &&
          <S.ExecutableMenuItem onClick={() => setDisplayFeatureCodesModal(!displayFeatureCodesModal)}>
            Beta Features
          </S.ExecutableMenuItem>
        }
        {Environment.isAWSCognito()
          ? <S.ExecutableMenuItem onClick={onLogoutClick}>Logout</S.ExecutableMenuItem>
          : <S.ExecutableLinkMenuItem tabIndex={0} href={Api.Session.logout}>Logout</S.ExecutableLinkMenuItem>
        }
      </Menu>

      {displayFeatureCodesModal && (isAdmin && !isImpersonating) && <RoleFeaturesModal closeCallback={onCloseFeaturesCallback} />}
      {displayFeatureCodesModal && (isDealer || isImpersonating) && <FeaturesModal closeCallback={onCloseFeaturesCallback} />}
    </>
  )
}

export default UserInfoMenu

const menuItem = ({ executable = false, sectionSplit = false, showFocusOutline = false } = {}) => (
  `
    display: block;
    padding: 10px 15px;
    color: ${color('tooltipText')};
    border-bottom: 1px solid ${color('menuItemSplit')};

    &:first-child {
      margin-top: -8px;
    }

    &:last-child {
      margin-bottom: -8px;
      border-bottom: none;
    }

    ${showFocusOutline && focusOutline()}

    ${executable && `
      cursor: pointer;
      font-weight: bold;
      width: 100%;
      text-align: left;

      &:hover {
        background-color: ${color('secondaryButtonHover')};
        color: ${color('primaryText')};
      }

      &:focus {
        color: ${color('primaryText')};
      }
    `}

    ${sectionSplit && `
      border-top: 1px solid ${color('menuItemSectionSplit')};
    `}
  `
)

const S = {
  Spinner: styled(Spinner)`
    margin: 10px auto;
    height: 30px;
    width: 30px;
  `,
  Button: styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${color('headerText')};
    font-weight: bold;
    text-transform: uppercase;
    height: 50px;
  `,
  Avatar: MuiStyled(Avatar)({
    background: color('border'),
    height: '40px',
    width: '40px',
    fontSize: '18pt',
    color: color('navLinkText')
  }),
  Username: styled.span`
    margin-left: 10px;
    font-size: 13pt;
    font-family: inter.bold;
    color: ${color('username')};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  `,
  CaretIcon: styled.i`
    font-size: 18px;
    position: relative;
  `,
  Image: styled.img.withConfig({
    shouldForwardProp: (prop) => !['displayMini'].includes(prop)
  }) <{ displayMini: boolean }>`
    width: max-content;
    max-width: 80px;
    max-height: 50px;
    ${({ displayMini }) => displayMini && 'max-width: 40px;'}
  `,
  NonExecutableMenuItem: styled.li`${menuItem({ showFocusOutline: true })}`,
  ExecutableMenuItem: styled(KeyboardAccessible)`${menuItem({ executable: true })}`,
  ExecutableLinkMenuItem: styled.a`${menuItem({ executable: true, sectionSplit: true })}`
}
